import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSession, onAuthStateChange, signUp as authSignUp, signIn as authSignIn, signOut as authSignOut } from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize session on mount
  useEffect(() => {
    let mounted = true;

    const initSession = async () => {
      try {
        const currentSession = await getSession();
        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error initializing session:', error);
        }
        if (mounted) {
          setSession(null);
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initSession();

    // Subscribe to auth state changes
    const { subscription } = onAuthStateChange((event, newSession) => {
      if (mounted) {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        
        // If session was cleared (sign out), ensure loading is false
        if (!newSession) {
          setLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const signUp = async (email, password, nickname = null) => {
    const result = await authSignUp(email, password, nickname);
    // Session will be updated via onAuthStateChange if auto-login occurs
    return result;
  };

  const signIn = async (email, password) => {
    const result = await authSignIn(email, password);
    // Session will be updated via onAuthStateChange
    return result;
  };

  const signOut = async () => {
    const result = await authSignOut();
    // Session will be cleared via onAuthStateChange
    return result;
  };

  const value = {
    session,
    user,
    loading,
    signUp,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

